import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Card, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import AppScriptAPI from '../../services/appscriptAPI';
import AppToast from '../../components/AppToast';
import LocationSelector from './components/LocationSelector';
import UserAvatar from './components/UserAvatar';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import './UserManagement.css';

const WORKING_LEVELS = [
  { value: 'all-island', label: 'National — DoSS Head Office' },
  { value: 'province',   label: 'Provincial Office' },
  { value: 'district',   label: 'District Office' },
  { value: 'dsd',        label: 'DS Division Office' },
];

const ACCESS_TYPES = [
  { value: 'viewer', label: 'Viewer — read only' },
  { value: 'editor', label: 'Editor — register & edit records' },
  { value: 'admin',  label: 'Admin — full system access' },
];

function EditUser() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const user = state?.user;
  const { userPermissions } = useUserPermissions();
  const isSelf = user?.email === userPermissions?.email;

  const [form, setForm] = useState({
    name:        user?.name        || '',
    designation: user?.designation || '',
    access_level: user?.access_level || 'dsd',
    access_type:  user?.access_type  || 'viewer',
  });

  const [locationData, setLocationData]         = useState(
    state?.locationData || { provinces: [], districts: {}, divisions: {} }
  );
  const [loadingLocations, setLoadingLocations] = useState(!state?.locationData);
  const splitCodes = val => val ? String(val).split(',').map(s => s.trim()).filter(Boolean) : [];

  const [selectedProvinces, setSelectedProvinces] = useState(splitCodes(user?.allowed_provinces));
  const [selectedDistricts, setSelectedDistricts] = useState(splitCodes(user?.allowed_districts));
  const [selectedDivisions, setSelectedDivisions] = useState(splitCodes(user?.allowed_dsds));

  const [saving, setSaving] = useState(false);
  const [toast, setToast]   = useState(null);

  useEffect(() => {
    if (state?.locationData) return;
    AppScriptAPI.getLocationDataForAnalytics()
      .then(res => setLocationData({
        provinces: res.provinces || [],
        districts: res.districts || {},
        divisions: res.divisions || {},
      }))
      .catch(() => {})
      .finally(() => setLoadingLocations(false));
  }, []);

  if (!user) {
    return (
      <div className="p-4">
        <Alert variant="danger">
          No user data found. <Button variant="link" className="p-0" onClick={() => navigate('/admin/users')}>Go back to User Management</Button>
        </Alert>
      </div>
    );
  }

  const handleWorkingLevelChange = (level) => {
    setForm(f => ({
      ...f,
      access_level: level,
      access_type: level === 'dsd' && f.access_type === 'admin' ? 'editor' : f.access_type,
    }));
    setSelectedProvinces([]);
    setSelectedDistricts([]);
    setSelectedDivisions([]);
  };

  const handleSave = async () => {
    setSaving(true);
    setToast(null);
    try {
      let allowedProvinces = '', allowedDistricts = '', allowedDsds = '';
      if (form.access_level === 'province') allowedProvinces = selectedProvinces.join(',');
      if (form.access_level === 'district') allowedDistricts = selectedDistricts.join(',');
      if (form.access_level === 'dsd')      allowedDsds      = selectedDivisions.join(',');

      const response = await AppScriptAPI.updateUserPermissions({
        email:             user.email,
        name:              form.name.trim(),
        designation:       form.designation.trim(),
        status:            user.status || 'active',
        access_level:      form.access_level,
        access_type:       form.access_type,
        allowed_provinces: allowedProvinces,
        allowed_districts: allowedDistricts,
        allowed_dsds:      allowedDsds,
      });

      if (!response.success) throw new Error(response.message || 'Failed to save changes');
      setToast({ type: 'success', msg: 'User updated successfully.' });
    } catch (e) {
      setToast({ type: 'danger', msg: 'Error: ' + e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Page header */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin/users')}>
          <i className="bi bi-arrow-left me-1"></i>Back
        </Button>
        <div>
          <h1 className="h3 mb-0">Edit User</h1>
          <small className="text-muted">Update profile, working level, and access permissions</small>
        </div>
      </div>

      <AppToast show={!!toast} type={toast?.type} message={toast?.msg} onClose={() => setToast(null)} />

      {/* Identity banner */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="d-flex align-items-center gap-3 py-3">
          <UserAvatar user={{ ...user, name: form.name || user.name }} large />
          <div>
            <div className="fw-semibold">{form.name || user.name || <span className="text-muted fst-italic">No name</span>}</div>
            <div className="small text-muted">{user.email}</div>
            {user.id && <div className="small text-muted">ID: {user.id}</div>}
          </div>
        </Card.Body>
      </Card>

      {/* Profile */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom py-3">
          <h6 className="mb-0 fw-semibold">
            <i className="bi bi-person me-2 text-primary"></i>Profile
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="um-field-label">Full Name</Form.Label>
                <Form.Control
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Enter full name"
                />
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="um-field-label">Designation</Form.Label>
                <Form.Control
                  value={form.designation}
                  onChange={e => setForm(f => ({ ...f, designation: e.target.value }))}
                  placeholder="e.g. Social Services Officer"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="um-field-label">Email Address</Form.Label>
                <Form.Control value={user.email} disabled />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Working Level & Location */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom py-3">
          <h6 className="mb-0 fw-semibold">
            <i className="bi bi-building me-2 text-primary"></i>Working Level &amp; Location
          </h6>
        </Card.Header>
        <Card.Body>
          {isSelf ? (
            <Alert variant="warning" className="mb-0">
              <i className="bi bi-lock-fill me-2"></i>
              You cannot change your own working level or location.
            </Alert>
          ) : (
            <>
              <Form.Group className="mb-4" style={{ maxWidth: 400 }}>
                <Form.Label className="um-field-label">Working Level</Form.Label>
                <Form.Select
                  value={form.access_level}
                  onChange={e => handleWorkingLevelChange(e.target.value)}
                >
                  {WORKING_LEVELS.map(l => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  The organisational level at which this user operates.
                </Form.Text>
              </Form.Group>

              <LocationSelector
                accessLevel={form.access_level}
                locationData={locationData}
                loadingLocations={loadingLocations}
                selectedProvinces={selectedProvinces}
                setSelectedProvinces={setSelectedProvinces}
                selectedDistricts={selectedDistricts}
                setSelectedDistricts={setSelectedDistricts}
                selectedDivisions={selectedDivisions}
                setSelectedDivisions={setSelectedDivisions}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {/* System Access */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom py-3">
          <h6 className="mb-0 fw-semibold">
            <i className="bi bi-shield-lock me-2 text-primary"></i>System Access
          </h6>
        </Card.Header>
        <Card.Body>
          {isSelf ? (
            <Alert variant="warning" className="mb-0">
              <i className="bi bi-lock-fill me-2"></i>
              You cannot change your own access role.
            </Alert>
          ) : (
            <Form.Group style={{ maxWidth: 400 }}>
              <Form.Label className="um-field-label">Access Role</Form.Label>
              <Form.Select
                value={form.access_type}
                onChange={e => setForm(f => ({ ...f, access_type: e.target.value }))}
              >
                {ACCESS_TYPES.filter(t => !(t.value === 'admin' && form.access_level === 'dsd')).map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
              {form.access_level === 'dsd' && (
                <Form.Text className="text-muted">Admin role is not available at DS Division level.</Form.Text>
              )}
            </Form.Group>
          )}
        </Card.Body>
      </Card>

      {/* Action bar */}
      <div className="d-flex justify-content-end gap-2 mb-5">
        <Button variant="outline-secondary" onClick={() => navigate('/admin/users')} disabled={saving}>
          Cancel
        </Button>
        <Button
          style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none' }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</>
            : <><i className="bi bi-floppy me-1"></i>Save Changes</>}
        </Button>
      </div>
    </div>
  );
}

export default EditUser;
